/* eslint-disable react-hooks/exhaustive-deps */
import '../styles/global.sass'
import React, { useEffect, useState } from 'react'
import { AppProps } from 'next/app'
import { ApolloProvider } from '@apollo/client'
import { useApollo } from 'gql/client'
import NProgress from 'nprogress'
import Router, { useRouter } from 'next/router'
import { useDispatch, useSelector } from 'react-redux'
import { api, isEmpty } from 'utils'
import { setPagina } from 'containers/pagina'
import useUtente from '@hooks/useUtente'
import { wrapper } from '../store'
import { initGTMdataLayer, setPage, setTrackingReady, updateGTMDataLayer } from 'utils/gtm'
import { CHECK_REVALIDATE } from '@utils/endpoints'
import { Roboto } from 'next/font/google'
import localFont from 'next/font/local'
import { UAParser } from 'ua-parser-js'
import { isBrowserSupported } from '@utils/user-agent'

NProgress.configure({
  minimum: 0.3,
  easing: 'ease',
  speed: 800,
  showSpinner: false,
})

Router.events.on('routeChangeStart', () => NProgress.start())
Router.events.on('routeChangeComplete', () => NProgress.done())
Router.events.on('routeChangeError', () => NProgress.done())

const fontArchivo = localFont({
  src: [
    {
      path: '../public/fonts/Archivo-Thin.ttf',
      weight: '100',
      style: 'normal',
    },
    {
      path: '../public/fonts/Archivo-ThinItalic.ttf',
      weight: '100',
      style: 'italic',
    },
    {
      path: '../public/fonts/Archivo-ExtraLight.ttf',
      weight: '200',
      style: 'normal',
    },
    {
      path: '../public/fonts/Archivo-ExtraLightItalic.ttf',
      weight: '200',
      style: 'italic',
    },
    {
      path: '../public/fonts/Archivo-Light.ttf',
      weight: '300',
      style: 'normal',
    },
    {
      path: '../public/fonts/Archivo-LightItalic.ttf',
      weight: '300',
      style: 'italic',
    },
    {
      path: '../public/fonts/Archivo-Regular.ttf',
      weight: '400',
      style: 'normal',
    },
    {
      path: '../public/fonts/Archivo-Italic.ttf',
      weight: '400',
      style: 'italic',
    },
    {
      path: '../public/fonts/Archivo-Medium.ttf',
      weight: '500',
      style: 'normal',
    },
    {
      path: '../public/fonts/Archivo-MediumItalic.ttf',
      weight: '500',
      style: 'italic',
    },
    {
      path: '../public/fonts/Archivo-SemiBold.ttf',
      weight: '600',
      style: 'normal',
    },
    {
      path: '../public/fonts/Archivo-SemiBoldItalic.ttf',
      weight: '600',
      style: 'italic',
    },
    {
      path: '../public/fonts/Archivo-Bold.ttf',
      weight: '700',
      style: 'normal',
    },
    {
      path: '../public/fonts/Archivo-BoldItalic.ttf',
      weight: '700',
      style: 'italic',
    },
    {
      path: '../public/fonts/Archivo-ExtraBold.ttf',
      weight: '800',
      style: 'normal',
    },
    {
      path: '../public/fonts/Archivo-ExtraBoldItalic.ttf',
      weight: '800',
      style: 'italic',
    },
    {
      path: '../public/fonts/Archivo-Black.ttf',
      weight: '900',
      style: 'normal',
    },
    {
      path: '../public/fonts/Archivo-BlackItalic.ttf',
      weight: '900',
      style: 'italic',
    },
  ],
})

// Per Pagebuilder
const fontRoboto = Roboto({
  weight: ['400', '700'],
  style: ['normal', 'italic'],
  subsets: ['latin'],
  display: 'swap',
})

function PageWrapper({ loginRequired = false, pageProps, children }) {
  const router = useRouter()
  const { pagina } = useSelector((state: any) => state?.pagina)
  // const { utente, loading } = useUtente()
  const dispatch = useDispatch()
  const [baseUrl, setBaseUrl] = useState(null)

  if (typeof window !== 'undefined' && process.env.NODE_ENV !== 'production') {
    const ReactDOM = require('react-dom')
    const axe = require('@axe-core/react')
    axe(React, ReactDOM, 1000)
  }

  useEffect(() => {
    const url = window.location.href
    const handleRouteChange = () => {
      setTrackingReady()
    }
    router.events.on('routeChangeStart', handleRouteChange)
    return () => {
      router.events.off('routeChangeStart', handleRouteChange)
    }
  }, [router])

  useEffect(() => {
    initGTMdataLayer()
  }, [])

  const getOnlyUrl = (url: string) => {
    var newUrl = url
    if (url.includes('?')) {
      newUrl = newUrl.split('?')[0]
    }
    if (newUrl.includes('#')) {
      newUrl = newUrl.split('#')[0]
    }
    return newUrl
  }

  // TODO
  // useEffect(() => {
  //   if (isEmpty(pageProps)) {
  //     dispatch(setPagina({ pagina: { chiave: '404', titolo: '404' } }))
  //   } else {
  //     if (getOnlyUrl(router.asPath) != baseUrl) {
  //       dispatch(setPagina(pageProps))
  //     }
  //     setBaseUrl(getOnlyUrl(router.asPath))
  //   }
  // }, [router.asPath])

  useEffect(() => {
    if (!isEmpty(pagina)) {
      updateGTMDataLayer(pagina, router)
      setPage(pagina, router.locale)
    }
  }, [pagina, router.asPath])

  useEffect(() => {
    if (process.env.NODE_ENV !== 'development' && pageProps?.revalidate)
      handleCheckRevalidate(
        '/' + router.locale + getOnlyUrl(router.asPath),
        pageProps.revalidate,
        window.location.hostname
      )
  }, [pageProps?.revalidate])

  const handleCheckRevalidate = async (path: string, revalidate: number, domain: string) => {
    await api.get(CHECK_REVALIDATE, { params: { path, revalidate, domain } })
  }

  return children
}

function MyApp({ Component, pageProps }: AppProps) {
  const router = useRouter()
  const apolloStore = useApollo(pageProps, null, router.locale)

  if (
    typeof window !== 'undefined' &&
    !window.location.pathname?.includes('/unsupported-browser.html')
  ) {
    const uaparser = new UAParser()
    const isSupported = isBrowserSupported(uaparser.getBrowser())
    if (!isSupported) {
      window.location.href = '/unsupported-browser.html'
      return null
    }
  }

  return (
    <>
      <style jsx global>{`
        html {
          --font-archivo: ${fontArchivo.style.fontFamily};
          --font-roboto: ${fontRoboto.style.fontFamily};
        }
      `}</style>
      <ApolloProvider client={apolloStore}>
        <PageWrapper loginRequired={Component.loginRequired || false} pageProps={pageProps}>
          <Component {...pageProps} />
        </PageWrapper>
      </ApolloProvider>
    </>
  )
}

export default wrapper.withRedux(MyApp)
